// src/pages/services/AIAutomation.jsx - Mobile Optimized
import React from 'react';
import { Link } from 'react-router-dom';
import RelatedServices from '../../components/RelatedServices';
import { Zap, Bot, Workflow, MessageSquare, FileText, ArrowLeft, Check, ArrowRight, TrendingUp, Target, Layers } from 'lucide-react';


export default function AIAutomation() {
  const services = [
    {
      icon: <Bot className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Intelligent Chatbots",
      description: "AI-powered conversational agents for customer service and engagement",
      features: ["Natural Language Processing", "Multi-language Support", "24/7 Availability", "CRM Integration"]
    },
    {
      icon: <Workflow className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Robotic Process Automation",
      description: "Automate repetitive tasks and streamline business processes",
      features: ["Process Mining", "Workflow Automation", "Task Scheduling", "Error Handling"]
    },
    {
      icon: <FileText className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Document Processing",
      description: "Automated extraction and processing of data from documents",
      features: ["OCR Technology", "Data Extraction", "Form Processing", "Invoice Automation"]
    },
    {
      icon: <Zap className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Workflow Optimization",
      description: "AI-driven optimization of business workflows and operations",
      features: ["Process Analysis", "Bottleneck Detection", "Performance Metrics", "Continuous Improvement"]
    }
  ];


  const automationTypes = [
    "Email Automation", "Data Entry Automation", "Report Generation", "Invoice Processing",
    "Customer Support", "Lead Qualification", "Appointment Scheduling", "Inventory Management"
  ];


  const technologies = [
    { name: "UiPath", icon: "🤖", category: "RPA Platform" },
    { name: "Blue Prism", icon: "🔷", category: "RPA Platform" },
    { name: "Automation Anywhere", icon: "⚡", category: "RPA Platform" },
    { name: "Power Automate", icon: "🔋", category: "Microsoft" },
    { name: "Python", icon: "🐍", category: "Programming" },
    { name: "TensorFlow", icon: "🧠", category: "AI Framework" }
  ];


  const benefits = [
    { title: "Cost Reduction", value: "Up to 70%", description: "Reduce operational costs" },
    { title: "Time Savings", value: "80% Faster", description: "Process completion time" },
    { title: "Accuracy", value: "99%+", description: "Error-free operations" },
    { title: "ROI", value: "6-12 Months", description: "Typical payback period" }
  ];


  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative py-12 sm:py-20 bg-gradient-to-br from-orange-600 to-yellow-600 text-white px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/services" className="inline-flex items-center gap-2 text-white hover:underline mb-4 sm:mb-6 text-xs sm:text-base">
            <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" /> Back to Services
          </Link>
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-center">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 leading-tight">
                AI Automation Solutions
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-orange-100 mb-6 sm:mb-8 leading-relaxed">
                Transform your business with intelligent automation. Reduce costs, eliminate errors, and free your team to focus on high-value work.
              </p>
              <div className="flex flex-col gap-3 sm:gap-4">
                <Link to="/contact" className="px-4 sm:px-8 py-2.5 sm:py-4 bg-white text-orange-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-center text-sm sm:text-base">
                  Start Automating
                </Link>
                <Link to="/portfolio" className="px-4 sm:px-8 py-2.5 sm:py-4 bg-transparent border-2 border-white rounded-lg font-semibold hover:bg-white/10 transition-colors text-center text-sm sm:text-base">
                  View Case Studies
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=600&h=400&fit=crop"
                alt="AI Automation"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>


      {/* Benefits Stats */}
      <section className="py-8 sm:py-16 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center group hover:scale-105 transition-transform p-2 sm:p-4">
                <div className="text-2xl sm:text-4xl font-black text-orange-600 mb-1 sm:mb-2 group-hover:scale-110 transition-transform">{benefit.value}</div>
                <div className="text-xs sm:text-lg font-bold text-gray-900 dark:text-white mb-1">{benefit.title}</div>
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{benefit.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Services */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              Our Automation Services
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto px-2">
              Comprehensive automation solutions tailored to your business needs
            </p>
          </div>


          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center text-orange-600 mb-3 sm:mb-4">
                  {service.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3">{service.title}</h3>
                <p className="text-xs sm:text-base text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">{service.description}</p>
                <ul className="space-y-1.5 sm:space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 dark:text-gray-400">
                      <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" /> {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* AI Automation Solutions Section */}
      <section className="py-12 sm:py-20 bg-gradient-to-br from-orange-600/10 via-yellow-600/10 to-orange-600/10 px-2 sm:px-4">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-3xl p-6 sm:p-12 shadow-2xl border border-orange-200/50 dark:border-orange-800/50">
            <div className="text-center mb-8 sm:mb-12">
              <Layers className="w-16 h-16 sm:w-28 sm:h-28 text-orange-600 mx-auto mb-4 sm:mb-8 opacity-80 animate-pulse" />
              <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-orange-600 via-yellow-600 to-orange-600 bg-clip-text text-transparent mb-4 sm:mb-6">
                Transform Your Business with Automation
              </h2>
              <p className="text-base sm:text-xl text-gray-700 dark:text-gray-300 mb-6 sm:mb-8 max-w-3xl mx-auto leading-relaxed px-2">
                We design and implement intelligent automation solutions tailored to your business needs. 
                From customer onboarding to document processing and customer support - 
                <span className="font-bold text-orange-600 dark:text-orange-400"> we build custom automation systems to streamline your operations</span>. 
                Let us help you increase efficiency and reduce manual work.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8 mb-6 sm:mb-12">
              <div className="group p-4 sm:p-8 bg-gradient-to-br from-orange-50 to-yellow-50 dark:from-orange-900/30 dark:to-yellow-900/30 rounded-2xl border border-orange-200/50 hover:shadow-2xl transition-all duration-300">
                <Bot className="w-12 h-12 sm:w-16 sm:h-16 text-orange-600 mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 text-center">Customer Onboarding</h3>
                <div className="space-y-1 sm:space-y-2 text-center">
                  <div className="text-lg sm:text-xl font-bold text-orange-600">Automated Workflows</div>
                  <div className="text-base sm:text-lg font-semibold text-yellow-600">KYC Automation</div>
                </div>
              </div>
              
              <div className="group p-4 sm:p-8 bg-gradient-to-br from-orange-50 to-yellow-50 dark:from-orange-900/30 dark:to-yellow-900/30 rounded-2xl border border-orange-200/50 hover:shadow-2xl transition-all duration-300">
                <FileText className="w-12 h-12 sm:w-16 sm:h-16 text-orange-600 mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 text-center">Document Processing</h3>
                <div className="space-y-1 sm:space-y-2 text-center">
                  <div className="text-lg sm:text-xl font-bold text-orange-600">Data Extraction</div>
                  <div className="text-base sm:text-lg font-semibold text-yellow-600">Form Processing</div>
                </div>
              </div>
              
              <div className="group p-4 sm:p-8 bg-gradient-to-br from-orange-50 to-yellow-50 dark:from-orange-900/30 dark:to-yellow-900/30 rounded-2xl border border-orange-200/50 hover:shadow-2xl transition-all duration-300">
                <MessageSquare className="w-12 h-12 sm:w-16 sm:h-16 text-orange-600 mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 text-center">Customer Support</h3>
                <div className="space-y-1 sm:space-y-2 text-center">
                  <div className="text-lg sm:text-xl font-bold text-orange-600">Intelligent Chatbots</div>
                  <div className="text-base sm:text-lg font-semibold text-yellow-600">Ticket Routing</div>
                </div>
              </div>
            </div>


            <div className="flex flex-col gap-3 sm:gap-6">
              <Link to="/contact" className="px-6 sm:px-12 py-3 sm:py-5 bg-gradient-to-r from-orange-600 via-yellow-600 to-orange-600 text-white rounded-2xl font-semibold text-base sm:text-xl shadow-2xl hover:shadow-3xl transform hover:-translate-y-2 transition-all duration-300 text-center tracking-wide">
                🚀 Start Your Project
              </Link>
              <Link to="/portfolio" className="px-6 sm:px-12 py-3 sm:py-5 border-2 sm:border-4 border-orange-600 text-orange-600 dark:text-orange-400 rounded-2xl font-semibold text-base sm:text-xl hover:bg-orange-600 hover:text-white transition-all duration-300 text-center tracking-wide">
                View Case Studies
              </Link>
            </div>
          </div>
        </div>
      </section>


      {/* Automation Types */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              What Can Be Automated?
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 px-2">
              Endless possibilities for automation across your organization
            </p>
          </div>


          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 max-w-5xl mx-auto">
            {automationTypes.map((type, index) => (
              <div
                key={index}
                className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl p-3 sm:p-6 shadow-lg border border-gray-200/50 dark:border-gray-700/50 text-center hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:scale-105 transition-all duration-300 group"
              >
                <p className="text-xs sm:text-base text-gray-900 dark:text-white font-bold group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">{type}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Technologies */}
      <section className="py-12 sm:py-20 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              Technologies We Use
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 px-2">
              Industry-leading automation platforms and frameworks
            </p>
          </div>


          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-6">
            {technologies.map((tech, index) => (
              <div key={index} className="bg-white dark:bg-gray-900 p-3 sm:p-6 rounded-xl shadow-lg text-center hover:scale-105 transition-all border border-gray-200/50 dark:border-gray-700/50 group">
                <div className="text-3xl sm:text-5xl mb-2 sm:mb-3 group-hover:scale-110 transition-transform">{tech.icon}</div>
                <h3 className="font-bold text-xs sm:text-base text-gray-900 dark:text-white mb-1">{tech.name}</h3>
                <p className="text-xs text-gray-600 dark:text-gray-400">{tech.category}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Process */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              Our Automation Process
            </h2>
          </div>


          <div className="grid md:grid-cols-5 gap-3 sm:gap-6">
            {[
              { step: "1", title: "Discovery", desc: "Identify automation opportunities" },
              { step: "2", title: "Design", desc: "Map workflows & processes" },
              { step: "3", title: "Development", desc: "Build automation bots" },
              { step: "4", title: "Testing", desc: "Validate & optimize" },
              { step: "5", title: "Deployment", desc: "Launch & monitor" }
            ].map((item, index) => (
              <div key={index} className="text-center group">
                <div className="w-14 h-14 sm:w-20 sm:h-20 bg-gradient-to-br from-orange-600 to-yellow-600 text-white rounded-full flex items-center justify-center text-lg sm:text-2xl font-black mx-auto mb-2 sm:mb-4 shadow-xl group-hover:scale-110 transition-transform">
                  {item.step}
                </div>
                <h3 className="text-sm sm:text-lg font-bold mb-1 sm:mb-2 text-gray-900 dark:text-white">{item.title}</h3>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Pricing */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-white via-gray-50 to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 sm:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 dark:text-white mb-3 sm:mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto px-2 leading-relaxed">
              Choose the plan that fits your business. Scale up anytime. No hidden fees.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto mb-12 sm:mb-20">
            {/* Starter Card */}
            <div className="group relative bg-white dark:bg-gray-900/80 backdrop-blur-sm p-6 sm:p-8 rounded-2xl shadow-lg dark:shadow-2xl border border-gray-200/60 dark:border-gray-700/60 hover:shadow-xl transition-all duration-300 flex flex-col hover:border-orange-200 dark:hover:border-orange-900/50">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-transparent dark:from-orange-900/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
              
              <h3 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white mb-2">Starter</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 font-medium">Perfect for small teams</p>
              
              {/* Price */}
              <div className="mb-8">
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-4xl sm:text-5xl font-black text-orange-600">$4,999</span>
                  <span className="text-gray-600 dark:text-gray-400 font-semibold">one-time</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl sm:text-3xl font-black text-orange-600">$499</span>
                  <span className="text-gray-600 dark:text-gray-400 font-semibold">/month</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium">Setup + Optional Support</p>
              </div>

              {/* Features */}
              <ul className="space-y-3 sm:space-y-4 mb-8 sm:mb-10 flex-grow">
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 font-bold" />
                  </div>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">1-2 Process Automation</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 font-bold" />
                  </div>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">AI Chatbot Setup</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 font-bold" />
                  </div>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">3 Months Support Included</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 font-bold" />
                  </div>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">Email & Chat Support</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 font-bold" />
                  </div>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">Basic Analytics</span>
                </li>
              </ul>
              
              <Link to="/contact" className="w-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-6 py-3 sm:py-4 rounded-xl font-bold hover:bg-gray-800 dark:hover:bg-gray-100 shadow-lg transition-all text-sm sm:text-base">
                Get Started Free
              </Link>
            </div>

            {/* Professional Card (Featured) */}
            <div className="group relative md:scale-105 bg-white dark:bg-gray-900/80 backdrop-blur-sm p-6 sm:p-8 rounded-2xl shadow-2xl dark:shadow-3xl border-2 border-orange-500 dark:border-orange-600 hover:shadow-2xl transition-all duration-300 flex flex-col">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-yellow-500/10 dark:from-orange-600/20 dark:to-yellow-600/20 rounded-2xl opacity-100 -z-10 blur-xl"></div>
              
              {/* Badge */}
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <div className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-black shadow-lg">
                  MOST POPULAR ⭐
                </div>
              </div>

              <h3 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white mb-2 mt-4 sm:mt-6">Professional</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-6 font-medium">Best for growing companies</p>
              
              {/* Price */}
              <div className="mb-8">
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-4xl sm:text-5xl font-black text-orange-600">$14,999</span>
                  <span className="text-gray-600 dark:text-gray-400 font-semibold">one-time</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl sm:text-3xl font-black text-orange-600">$1,499</span>
                  <span className="text-gray-600 dark:text-gray-400 font-semibold">/month</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium">Setup + Recommended Support</p>
              </div>

              {/* Features */}
              <ul className="space-y-3 sm:space-y-4 mb-8 sm:mb-10 flex-grow">
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600 font-bold" />
                  </div>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-semibold">5-10 Process Automation</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600 font-bold" />
                  </div>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-semibold">Advanced AI Features</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600 font-bold" />
                  </div>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-semibold">6 Months Support Included</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600 font-bold" />
                  </div>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-semibold">Priority Support (24/5)</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600 font-bold" />
                  </div>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-semibold">Custom Integrations</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600 font-bold" />
                  </div>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-semibold">Advanced Analytics & Reports</span>
                </li>
              </ul>

              <Link to="/contact" className="w-full bg-gradient-to-r from-orange-600 to-yellow-600 text-white px-6 py-3 sm:py-4 rounded-xl font-bold hover:from-orange-700 hover:to-yellow-700 shadow-lg hover:shadow-xl transition-all text-sm sm:text-base transform hover:-translate-y-1">
                Start Your Trial
              </Link>
            </div>

            {/* Enterprise Card */}
            <div className="group relative bg-white dark:bg-gray-900/80 backdrop-blur-sm p-6 sm:p-8 rounded-2xl shadow-lg dark:shadow-2xl border border-gray-200/60 dark:border-gray-700/60 hover:shadow-xl transition-all duration-300 flex flex-col hover:border-blue-200 dark:hover:border-blue-900/50">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-transparent dark:from-blue-900/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
              
              <h3 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white mb-2">Enterprise</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 font-medium">For large-scale operations</p>
              
              {/* Price */}
              <div className="mb-8">
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-4xl sm:text-5xl font-black text-blue-600">$5,000</span>
                  <span className="text-gray-600 dark:text-gray-400 font-semibold">/month</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium">Starting retainer • Custom scoping available</p>
              </div>

              {/* Features */}
              <ul className="space-y-3 sm:space-y-4 mb-8 sm:mb-10 flex-grow">
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 font-bold" />
                  </div>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">Unlimited Process Automation</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 font-bold" />
                  </div>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">Dedicated Team (2-3 Specialists)</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 font-bold" />
                  </div>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">24/7 Premium Support & SLA</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 font-bold" />
                  </div>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">Monthly Strategy & Optimization</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 font-bold" />
                  </div>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">Enterprise Analytics Dashboard</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 font-bold" />
                  </div>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">Custom Integration & API Access</span>
                </li>
              </ul>

              <Link to="/contact" className="w-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-6 py-3 sm:py-4 rounded-xl font-bold hover:bg-gray-800 dark:hover:bg-gray-100 shadow-lg transition-all text-sm sm:text-base">
                Let's Talk
              </Link>
            </div>
          </div>

          {/* Comparison Features */}
          <div className="max-w-5xl mx-auto mb-12 sm:mb-16">
            <h3 className="text-center text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-8">All Plans Include</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {[
                { icon: "✓", text: "Free Consultation" },
                { icon: "✓", text: "Process Mapping" },
                { icon: "✓", text: "Architecture Design" },
                { icon: "✓", text: "On-time Delivery" }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800/50 rounded-lg border border-gray-200/50 dark:border-gray-700/50">
                  <span className="text-lg text-green-600 font-bold">{item.icon}</span>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ Section */}
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 rounded-2xl border border-orange-200/50 dark:border-orange-800/50 p-6 sm:p-10">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">Frequently Asked Questions</h3>
            <div className="space-y-4 sm:space-y-5">
              <details className="group cursor-pointer">
                <summary className="flex items-center justify-between font-semibold text-gray-900 dark:text-white py-3 px-4 bg-white dark:bg-gray-800/50 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <span className="text-sm sm:text-base">Can I upgrade or downgrade plans?</span>
                  <span className="group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <p className="text-sm text-gray-600 dark:text-gray-300 p-4 bg-white/50 dark:bg-gray-800/30">Yes! You can upgrade, downgrade, or cancel anytime. Changes take effect at your next billing cycle with prorated adjustments.</p>
              </details>
              <details className="group cursor-pointer">
                <summary className="flex items-center justify-between font-semibold text-gray-900 dark:text-white py-3 px-4 bg-white dark:bg-gray-800/50 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <span className="text-sm sm:text-base">What's included in the setup fee?</span>
                  <span className="group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <p className="text-sm text-gray-600 dark:text-gray-300 p-4 bg-white/50 dark:bg-gray-800/30">The setup fee covers initial configuration, bot/automation development, testing, deployment, and initial training for your team.</p>
              </details>
              <details className="group cursor-pointer">
                <summary className="flex items-center justify-between font-semibold text-gray-900 dark:text-white py-3 px-4 bg-white dark:bg-gray-800/50 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <span className="text-sm sm:text-base">Is the monthly support mandatory?</span>
                  <span className="group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <p className="text-sm text-gray-600 dark:text-gray-300 p-4 bg-white/50 dark:bg-gray-800/30">For Starter & Professional plans, monthly support is optional after the included months. Enterprise plans require ongoing support.</p>
              </details>
              <details className="group cursor-pointer">
                <summary className="flex items-center justify-between font-semibold text-gray-900 dark:text-white py-3 px-4 bg-white dark:bg-gray-800/50 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <span className="text-sm sm:text-base">Do you offer custom pricing?</span>
                  <span className="group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <p className="text-sm text-gray-600 dark:text-gray-300 p-4 bg-white/50 dark:bg-gray-800/30">Absolutely! Contact our sales team for custom requirements, volume discounts, or specialized enterprise packages.</p>
              </details>
            </div>
          </div>
        </div>
      </section>


      {/* CTA */}
      <section className="py-12 sm:py-20 bg-gradient-to-br from-orange-600 to-yellow-600 text-white px-2 sm:px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Target className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 animate-pulse" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-4 sm:mb-6">
            Ready to Automate Your Business?
          </h2>
          <p className="text-base sm:text-xl text-orange-100 mb-6 sm:mb-8 px-2">
            Join hundreds of businesses saving time and money with intelligent automation
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-6 sm:px-10 py-3 sm:py-5 bg-white text-orange-600 rounded-2xl font-black text-base sm:text-xl shadow-2xl hover:shadow-3xl hover:-translate-y-1 transition-all duration-300"
          >
            <span>🚀 Schedule Free Assessment</span>
            <ArrowRight className="w-4 h-4 sm:w-6 sm:h-6" />
          </Link>
        </div>
      </section>
      <div className="mt-16">
      <RelatedServices currentService="ai-automation" />
      </div>
    </div>
  );
}